/**
 * @file index.jsx
 *
 * @component ProjectPage
 * @description This component renders the main page for projects, including a sidebar drawer
 *              for selecting projects, and a button that allows a user to create a new project.
 *
 * @subcomponent SideBarDrawer - Renders a project selector for navigating between different projects,
 *                               and a button for creating new projects.
 * @subcomponent ProjectContent - Displays project-specific views for the selected project.
 */

import { useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useSelector } from "react-redux";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';

import { useTheme } from "@mui/material/styles";

import ProjectContent from "../../components/Project/ProjectContent"
import ProjectModal from "../../components/Project/ProjectModal/ProjectModal";
import ProjectSelector from "../../components/Project/ProjectSelector";

import SidebarDrawer from "./SidebarDrawer";
import { useProjectPage } from "./useProjectPage";

const paperProps = {
  flexGrow: 1,
  display: "flex",
  flexDirection: "column",
  width: "100%",
  height: "auto",
  m: 1,
  maxHeight: 'calc(100vh - 112px)',
  overflowY: "auto",
};

const ProjectPage = ({
  selectedSiteId: initialSiteId = null,
  initialFile = null,
}) => {
  const theme = useTheme();

  const drawerRef = useRef(null);

  const activeSiteId = useSelector(state => state.ui.toggleWorkspaces.workspace?.id || initialSiteId);

  const {
    activeProjectId,
    onProjectCreated,
    onProjectUpdated,
    projectLookup,
    projectsLoading,
    projectsError,
    selectActiveProject,
  } = useProjectPage(activeSiteId);

  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);

  const closeProjectModal = () => { setIsProjectModalOpen(false); };
  const openProjectModal = () => { setIsProjectModalOpen(true); };

  const handleProjectCreated = (project) => {
    closeProjectModal();
    onProjectCreated(project);
  };

  /**
   * Displays loading, error, or empty state for project content.
   * @returns {JSX.Element}
   */
  const renderProjectState = () => {
    if (projectsLoading) {
      return <CircularProgress color="inherit" size={60} sx={{ verticalAlign: 'middle' }} />;
    }

    if ((projectsError || !projectsLoading) && (projectLookup?.length === 0)) {
      return <FormattedMessage id="projectSelector.noProject" />;
    }

    return null;
  }

  const isProjectContentAvailable = Boolean(activeProjectId) && !projectsLoading && !projectsError;

  return (
    <Box sx={{ display: "flex", backgroundColor: theme.palette.background.cream }}>
      <SidebarDrawer ref={drawerRef}>
        <ProjectSelector
          disabled={!activeSiteId}
          onChangeProject={selectActiveProject}
          projectLookup={projectLookup}
          selectedProjectId={activeProjectId}
          role="listbox"
        />
        <Button
          disabled={!activeSiteId}
          onClick={openProjectModal}
          sx={{ mt: 1 }}
          variant="contained"
          aria-label="Create new project"
        >
          <FormattedMessage id="projectSelector.newProject" />
        </Button>
      </SidebarDrawer>
      <Paper sx={paperProps}>
        {!isProjectContentAvailable &&
          <Box color="text.secondary" component="h1" m="auto" aria-live="polite">
            {renderProjectState()}
          </Box>
        }
        {isProjectContentAvailable && (
          <ProjectContent
            drawerRef={drawerRef}
            initialFile={initialFile}
            onUpdated={onProjectUpdated}
            projectId={activeProjectId}
            siteId={activeSiteId}
          />
        )}
      </Paper>
      {isProjectModalOpen && (
        <ProjectModal
          onClose={closeProjectModal}
          onSaved={handleProjectCreated}
          open={isProjectModalOpen}
          siteId={activeSiteId}
        />
      )}
    </Box>
  );
};

ProjectPage.displayName = "ProjectPage";

export default ProjectPage;
